/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chat, KolProfileMessage as KPM } from '@trinity/chat';
import {
  ChatCitationType,
  ChatHintType,
  ChatResponseType,
  chatDateFiltersMap,
  ReportFormat,
} from '@trinity/chat';
import { LearnMore } from '@trinity/chat-components/components/Chat/LearnMore';
import { getCitationsDisplayCount } from '@trinity/chat-components/components/Chat.Message.Citations/utils';
import { Citation } from '@trinity/chat-components/components/Markdown/Citations';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { StyledAnchor } from '@trinity/chat-components/components/presentation/Link';
import { MessageQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { GroupFeaturesContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useHasPredictiveScheduling } from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import {
  TextToSpeechStateContext,
  VoiceModeContext,
  useInitiateQuerySpeechToTextNullSafe,
  useStopTextToSpeechContextNullSafe,
} from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { useHover } from '@trinity/chat-components/hooks/useHover';
import { formatPlurality, possessive, pluralize, formatPhoneNumber } from '@trinity/chat-components/utils';
import {
  ActiveChatSessionContext,
  CitationsContext,
  KolFollowupQuestionsContext,
  KolProfileContext,
  KolSearchIdContext,
  MessageCitationsToggleContext,
} from '../context';
import { useDownloadKolProfile } from '../hooks/useDownloadKolProfile';
import { useSelectedQueryParentIdentifier } from '../hooks/useSelectedQueryParentIdentifier';
import { useSetChatInputText } from '../hooks/useSetChatInputText';
import { useSubmitQueryMutation } from '../hooks/useSubmitQueryMutation';
import { ProfileAccordionContainer } from '../KolSearch.Profile.Accordion';
import { AITooltip } from '../KolSearch.Profile.AITooltip';
import { AvailabilityInsights } from '../KolSearch.Profile.Availability';
import { KolRecommendationReason } from '../KolSearch.Recommended';
import { CopyButton } from '../Message.CopyToClipboard';
import { BaseDownloadButton } from '../Message.Download';
import { ReadAloudButton } from '../Message.ReadAloud';
import { BasicSystemUserMessage } from '../Message.System.Basic';
import { SystemMessageContainer } from '../Message.System.Container';
import { buildQuickActions } from './quick-actions';
import {
  Copy,
  Footer,
  Guidelines,
  Label,
  MessageBody,
  MessageContainer,
  ProfileItem,
  QuickActionsList,
  Recommendations,
  StyledCitationsPane,
  StyledList,
  StyledListItem,
  StyledMessageCitations,
  StyledRelatedQuestions,
} from './shared-styles';
import { renderProfileWidget } from './widgets';

function isDefined<T>(value: T | null | undefined): value is T {
  return value != null;
}

function getCitationOrdinals(items: Chat.Citation[]): string[] {
  return items
    .map((item) => item.ordinal)
    .filter((ordinal): ordinal is number => ordinal != null)
    .map((ordinal) => ordinal.toString());
}

function getCitationOrdinal(item: Chat.Citation | undefined): string[] {
  const resolvedOrdinal = item?.ordinal;
  if (resolvedOrdinal == null) {
    return [];
  }

  return [resolvedOrdinal.toString()];
}

type LeadershipItem = {
  value: string;
  learnMore: string;
};

type Props = {
  followupQuestions: string[];
  kolSearchId?: number;
  queryId?: number;
  profile: KPM;
};

function buildLeadershipItems(
  profile: KPM,
  groupFeatures: { hcpProfileElements: { clinicalTrials?: boolean; publications?: boolean } }
): LeadershipItem[] {
  const congressAbstractionCutoffs = [300, 250, 150, 100, 50];
  const estAbstractions = congressAbstractionCutoffs.find((cutoff) => profile.numCongressAbstracts >= cutoff);

  return [
    profile.numClinicalTrials > 0 && groupFeatures.hcpProfileElements.clinicalTrials
      ? {
          value: `${profile.numClinicalTrials} ${formatPlurality('Clinical Trial', profile.numClinicalTrials)}`,
          learnMore: `I'd like to learn more about the clinical trials ${profile.name} participated in.`,
        }
      : null,
    profile.numPublications > 0 &&
    profile.numCongressAbstracts > 0 &&
    groupFeatures.hcpProfileElements.publications
      ? {
          value: `${profile.numPublications} Publications plus ${estAbstractions ? `> ${estAbstractions}` : profile.numCongressAbstracts} congress abstracts`,
          learnMore: `I'd like to learn more about the publications ${profile.name} has appeared in.`,
        }
      : null,
    profile.numPublications > 0 &&
    profile.numCongressAbstracts === 0 &&
    groupFeatures.hcpProfileElements.publications
      ? {
          value: `${profile.numPublications} ${formatPlurality('Publication', profile.numPublications)}`,
          learnMore: `I'd like to learn more about the publications ${profile.name} has appeared in.`,
        }
      : null,
    profile.numLeadAuthorPublications > 0 && groupFeatures.hcpProfileElements.publications
      ? {
          value: `${profile.numLeadAuthorPublications} ${formatPlurality('Lead Author Publication', profile.numLeadAuthorPublications)}`,
          learnMore: `I'd like to learn more about publications where ${profile.name} was the lead author.`,
        }
      : null,
  ].filter(isDefined);
}

function buildTotalsText(value: number) {
  return value ? `(${value} total)` : '';
}

function toInteractionCount(value: number | null | undefined): number {
  return value ?? 0;
}

function getInteractionCounts(profile: KPM) {
  const { interactions } = profile;

  if (interactions.allTime) {
    const { allTime, lookback, lookbackPeriod } = interactions;

    return {
      fbms: toInteractionCount(allTime.numFbmsInteractions),
      contactCenter: toInteractionCount(allTime.numContactCenterInteractions),
      medical: toInteractionCount(allTime.numMedicalInsights),
      documented: toInteractionCount(allTime.numDocumentedInterests),
      withMe: toInteractionCount(allTime.withMe),
      lookbackFbms: toInteractionCount(lookback.numFbmsInteractions),
      lookbackContactCenter: toInteractionCount(lookback.numContactCenterInteractions),
      lookbackMedical: toInteractionCount(lookback.numMedicalInsights),
      lookbackDocumented: toInteractionCount(lookback.numDocumentedInterests),
      lookbackWithMe: toInteractionCount(lookback.withMe),
      lookbackPeriod: lookbackPeriod ? ` in the ${chatDateFiltersMap[lookbackPeriod].toLowerCase()}` : '',
    };
  }

  return {
    fbms: toInteractionCount(interactions.numFbmsInteractions),
    contactCenter: toInteractionCount(interactions.numContactCenterInteractions),
    medical: toInteractionCount(interactions.numMedicalInsights),
    documented: toInteractionCount(interactions.numDocumentedInterests),
    withMe: toInteractionCount(interactions.withMe),
    lookbackFbms: 0,
    lookbackContactCenter: 0,
    lookbackMedical: 0,
    lookbackDocumented: 0,
    lookbackWithMe: 0,
    lookbackPeriod: '',
  };
}

function buildInteractionItems(profile: KPM, citations: Chat.Citation[]) {
  const counts = getInteractionCounts(profile);
  const { lookbackPeriod } = counts;

  const fbmsCitations = citations.filter((f) => f.type === ChatCitationType.FbmsInteractions);
  const contactCenterCitations = citations.filter((f) => f.type === ChatCitationType.CallCenterInteractions);
  const medicalInsightsCitations = citations.filter((f) => f.type === ChatCitationType.MedicalInsights);
  const documentedInterestsCitations = citations.filter(
    (f) => f.type === ChatCitationType.DocumentedInterests
  );

  return [
    {
      citations: getCitationOrdinals(fbmsCitations),
      value:
        counts.lookbackFbms > 0
          ? `${counts.lookbackFbms} ${formatPlurality('interaction', counts.lookbackFbms)} with MSLs${lookbackPeriod}. ${buildTotalsText(counts.fbms)}`
          : `No interactions${lookbackPeriod}. ${buildTotalsText(counts.fbms)}`,
      learnMore:
        counts.fbms > 0
          ? `I would like to learn more about the interactions ${profile.name} has had with MSLs.`
          : undefined,
    },
    {
      citations: getCitationOrdinals(contactCenterCitations),
      value:
        counts.lookbackContactCenter > 0
          ? `${counts.lookbackContactCenter} contact center ${formatPlurality('interaction', counts.lookbackContactCenter)}${lookbackPeriod}. ${buildTotalsText(counts.contactCenter)}`
          : `No contact center interactions${lookbackPeriod}. ${buildTotalsText(counts.contactCenter)}`,
      learnMore:
        counts.contactCenter > 0
          ? `I would like to learn more about the interactions ${profile.name} has had with the contact center.`
          : undefined,
    },
    {
      citations: getCitationOrdinals(medicalInsightsCitations),
      value:
        counts.lookbackMedical > 0
          ? `${counts.lookbackMedical} ${formatPlurality('medical insight', counts.lookbackMedical)} expressed${lookbackPeriod}. ${buildTotalsText(counts.medical)}`
          : `No medical insights expressed${lookbackPeriod}. ${buildTotalsText(counts.medical)}`,
      learnMore:
        counts.medical > 0
          ? `I would like to learn more about the medical insights ${profile.name} has expressed.`
          : undefined,
    },
    {
      citations: getCitationOrdinals(documentedInterestsCitations),
      value:
        counts.lookbackDocumented > 0
          ? `${counts.lookbackDocumented} ${formatPlurality('documented interest', counts.lookbackDocumented)}${lookbackPeriod}. ${buildTotalsText(counts.documented)}`
          : `No documented interests ${lookbackPeriod}. ${buildTotalsText(counts.documented)}`,
      learnMore:
        counts.documented > 0
          ? `I would like to learn more about the documented interests ${profile.name} has expressed.`
          : undefined,
    },
    {
      value:
        counts.lookbackWithMe > 0
          ? `You have interacted with HCP ${counts.lookbackWithMe} ${pluralize('time', counts.lookbackWithMe)}${lookbackPeriod}. ${buildTotalsText(counts.withMe)}`
          : `You have not interacted with HCP${lookbackPeriod}. ${buildTotalsText(counts.withMe)}`,
      learnMore:
        counts.withMe > 0
          ? `I would like to learn more about any recent interactions I've had with ${profile.name}.`
          : undefined,
    },
  ];
}

function buildTopicItems(profile: KPM, citations: Chat.Citation[]): ListItemProps[] {
  const dltCitation = citations.filter((f) => f.type === ChatCitationType.DigitalLeaderTopic);
  const cltCitation = citations.filter((f) => f.type === ChatCitationType.ClinicalLeaderTopic);
  const sltCitation = citations.filter((f) => f.type === ChatCitationType.ScientificLeaderTopic);

  const items: ListItemProps[] = [];

  if (profile.numScientificFocusAreas > 0) {
    items.push({
      citations: getCitationOrdinals(sltCitation),
      value: `${profile.numScientificFocusAreas} Scientific Leader Topics`,
      learnMore: `I would like to learn more about ${possessive(profile.name)} scientific leader topics.`,
    });
  }

  if (profile.numClinicalFocusAreas > 0) {
    items.push({
      citations: getCitationOrdinals(cltCitation),
      value: `${profile.numClinicalFocusAreas} Clinical Leader Topics`,
      learnMore: `I would like to learn more about ${possessive(profile.name)} clinical leader topics.`,
    });
  }

  if (profile.numDigitalFocusAreas > 0) {
    items.push({
      citations: getCitationOrdinals(dltCitation),
      value: `${profile.numDigitalFocusAreas} Digital Leader Topics`,
      learnMore: `I would like to learn more about ${possessive(profile.name)} digital leader topics.`,
    });
  }

  return items;
}

function buildContactInformationItems(profile: KPM): ListItemProps[] {
  if (!profile.contact) {
    return [];
  }

  const items: ListItemProps[] = [];

  if (profile.contact.email) {
    items.push({
      value: (
        <>
          <BulletedLabel>Email</BulletedLabel>: {profile.contact.email}
        </>
      ),
    });
  }

  if (profile.contact.phone) {
    items.push({
      value: (
        <>
          <BulletedLabel>Phone</BulletedLabel>: {formatPhoneNumber(profile.contact.phone)}
        </>
      ),
    });
  }

  if (profile.contact.address) {
    items.push({
      value: (
        <>
          <BulletedLabel>Address</BulletedLabel>: {profile.contact.address}
        </>
      ),
    });
  }

  return items;
}

type RenderTextField = (props: RenderTextProps) => ReactNode;
type RenderTextListField = (props: RenderTextListProps) => ReactNode;

function renderProfileTextField({ citations = [], label, value }: RenderTextProps): ReactNode {
  if (!value) {
    return null;
  }

  return (
    <ProfileItem>
      <Label>
        {label}
        {!!citations.length && citations.map((c) => <Citation key={c} value={c} />)}
      </Label>
      {value}
    </ProfileItem>
  );
}

type RenderProfileTextListArgs = {
  hoveringSection: string | null;
  handleMouseEnterSection: (label: string) => () => void;
  handleMouseLeaveSection: () => void;
  showDirectSubmitLearnMore: boolean;
  onDirectSubmitLearnMore: (question: string) => void;
};

function renderProfileTextList(
  {
    hoveringSection,
    handleMouseEnterSection,
    handleMouseLeaveSection,
    showDirectSubmitLearnMore,
    onDirectSubmitLearnMore,
  }: RenderProfileTextListArgs,
  { citations = [], label, items, learnMore, aiGenerated }: RenderTextListProps
): ReactNode {
  if (!items.length) {
    return null;
  }

  const isHoveringOver = hoveringSection === label;

  return (
    <ProfileItem onMouseEnter={handleMouseEnterSection(label)} onMouseLeave={handleMouseLeaveSection}>
      <ProfileAccordionContainer
        profileKey={label}
        renderChildren={() => (
          <StyledList>
            {items.map((item, index) => (
              <TextListItem
                key={index}
                citations={item.citations}
                value={item.value}
                learnMore={item.learnMore}
                showDirectSubmitLearnMore={showDirectSubmitLearnMore}
                onDirectSubmitLearnMore={onDirectSubmitLearnMore}
              />
            ))}
          </StyledList>
        )}
      >
        <Label>
          {label}
          {!!citations.length && citations.map((c) => <Citation key={c} value={c} />)}
          {learnMore && (
            <LearnMore
              open={isHoveringOver}
              question={learnMore}
              showDirectSubmit={showDirectSubmitLearnMore}
              onDirectSubmit={onDirectSubmitLearnMore}
            />
          )}
          {aiGenerated && <AITooltip helpText={aiGenerated} />}
        </Label>
      </ProfileAccordionContainer>
    </ProfileItem>
  );
}

function renderLeadershipFieldSection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  hcpProfileElements: { clinicalTrials?: boolean; publications?: boolean };
  renderTextListField: RenderTextListField;
}): ReactNode {
  const { profile, citations, hcpProfileElements, renderTextListField } = args;
  const items = buildLeadershipItems(profile, {
    hcpProfileElements,
  });

  if (!items.length) {
    return null;
  }

  const clinicalTrialsCitation = citations.find((f) => f.type === ChatCitationType.ClinicalTrial);
  const publicationCitation = citations.find((f) => f.type === ChatCitationType.Publication);

  return renderTextListField({
    citations: [publicationCitation, clinicalTrialsCitation]
      .filter(isDefined)
      .flatMap((citation) => getCitationOrdinal(citation)),
    label: 'Clinical & Research Activity',
    items,
  });
}

function renderInteractionsSummarySection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  renderTextListField: RenderTextListField;
}): ReactNode {
  const { profile, citations, renderTextListField } = args;

  if (!profile.interactions && profile.id !== 15130806) {
    return null;
  }

  const label = `Interactions Summary (${profile.interactions.allTime?.total} total)`;
  const items = buildInteractionItems(profile, citations);

  return renderTextListField({ label, items });
}

function renderTopicsSection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  renderTextListField: RenderTextListField;
}): ReactNode {
  const { profile, citations, renderTextListField } = args;
  const items = buildTopicItems(profile, citations);

  if (!items.length) {
    return null;
  }

  return renderTextListField({ label: 'Leader Topics', items });
}

function renderConferencePresenceSection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  renderTextListField: RenderTextListField;
}): ReactNode {
  const { profile, citations, renderTextListField } = args;
  const label = 'Conference Presence';
  const learnMore = `I'd like to learn more about ${possessive(profile.name)} conference presence.`;
  const conferenceCitation = citations.find((f) => f.type === ChatCitationType.Conference);

  if (profile.upcomingEvents?.length) {
    return renderTextListField({
      citations: getCitationOrdinal(conferenceCitation),
      label,
      items: profile.upcomingEvents.map((e) => ({ value: e.name })),
      learnMore,
    });
  }

  if (profile.events?.length) {
    return renderTextListField({
      citations: getCitationOrdinal(conferenceCitation),
      label,
      items: profile.events.map((e) => {
        const roles = e.roles
          .map((r) => r.position)
          .map((i, v, a) => (a.indexOf(i) === v ? i : null))
          .filter(Boolean)
          .join(', ');
        return {
          value: `${e.name} (${roles})`,
        };
      }),
      learnMore,
    });
  }

  return null;
}

function renderRecommendationReasonSection(profile: KPM): ReactNode {
  if (!profile.recommendations?.length) {
    return null;
  }

  const label = `Recommended because patients have the following unmet medical needs:`;

  return (
    <ProfileItem>
      <Label>{label}</Label>
      <Recommendations>
        {profile.recommendations.map((r, i) => (
          <KolRecommendationReason value={r} key={i} />
        ))}
      </Recommendations>
    </ProfileItem>
  );
}

function renderContactDetailsSection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  renderTextListField: RenderTextListField;
}): ReactNode {
  const { profile, citations, renderTextListField } = args;
  const items = buildContactInformationItems(profile);

  if (!items.length) {
    return null;
  }

  const citation = citations.find((f) => f.type === ChatCitationType.ContactDetail);

  return renderTextListField({
    citations: getCitationOrdinal(citation),
    label: 'Contact Information',
    items,
  });
}

function renderPrimaryAffiliationSection(args: {
  profile: KPM;
  citations: Chat.Citation[];
  renderTextField: RenderTextField;
}): ReactNode {
  const { profile, citations, renderTextField } = args;
  const citation = citations.find((f) => f.type === ChatCitationType.Affiliation);

  return renderTextField({
    citations: getCitationOrdinal(citation),
    label: 'Primary Affiliation',
    value: profile.affiliations.map((m) => m.name).join('\n'),
  });
}

function getProfileDisplayName(profile: KPM): string {
  if (!profile.qualifications.length) {
    return profile.name;
  }

  const sortedQualifications = [...profile.qualifications].sort((a, b) => a.localeCompare(b));
  return `${profile.name}, ${sortedQualifications.join(', ')}`;
}

function renderQuickActionsSection(args: {
  actions: ReturnType<typeof buildQuickActions>;
  onActionClick: (value: string) => void;
}): ReactNode {
  const { actions, onActionClick } = args;

  if (!actions.length) {
    return null;
  }

  return (
    <ProfileItem>
      <ProfileAccordionContainer
        profileKey="quick-actions"
        renderChildren={() => (
          <QuickActionsList>
            {actions.map((action, i) => (
              <MessageQueryHint
                key={`${i}`}
                hint={{
                  type: ChatHintType.Text,
                  displayValue: action.displayValue,
                }}
                onClick={() => {
                  onActionClick(action.value);
                }}
              />
            ))}
          </QuickActionsList>
        )}
      >
        <Label>Quick Actions</Label>
      </ProfileAccordionContainer>
    </ProfileItem>
  );
}

function renderProfileConfigChildren(args: {
  displayName: string;
  profile: KPM;
  renderTextField: RenderTextField;
  renderPrimaryAffiliation: () => ReactNode;
  sectionRenderers: Record<string, () => ReactNode>;
  renderQuickActions: () => ReactNode;
  renderGuidelines: () => ReactNode;
}): ReactNode {
  const {
    displayName,
    profile,
    renderTextField,
    renderPrimaryAffiliation,
    sectionRenderers,
    renderQuickActions,
    renderGuidelines,
  } = args;

  return (
    <>
      {renderTextField({ label: 'Name', value: displayName })}
      {renderTextField({ label: 'Designation', value: profile.title })}
      {renderTextField({ label: 'Specialty', value: profile.specialty })}
      {renderPrimaryAffiliation()}
      {profile.profileConfig?.sections.map((section) => {
        const renderer = sectionRenderers[section.key];
        const sectionWidgetData = profile.widgetData?.[section.key];

        // Built-in section with its own renderer
        if (renderer) {
          return (
            <Fragment key={section.key}>
              {renderer()}
              {sectionWidgetData?.widgets.map((w) => (
                <ProfileItem key={w.id}>{renderProfileWidget(w)}</ProfileItem>
              ))}
            </Fragment>
          );
        }

        // Custom section — render collapsible header with title + tooltips
        if (sectionWidgetData?.widgets.length) {
          return (
            <ProfileItem key={section.key}>
              <ProfileAccordionContainer
                profileKey={section.key}
                renderChildren={() => (
                  <>
                    {sectionWidgetData.widgets.map((w) => (
                      <Fragment key={w.id}>{renderProfileWidget(w)}</Fragment>
                    ))}
                  </>
                )}
              >
                <Label>
                  {section.name}
                  {section.infoTooltip && <AITooltip helpText={section.infoTooltip} />}
                  {section.aiTooltip && <AITooltip helpText={section.aiTooltip} />}
                </Label>
              </ProfileAccordionContainer>
            </ProfileItem>
          );
        }

        return null;
      })}
      {renderQuickActions()}
      {renderGuidelines()}
    </>
  );
}

function renderDefaultProfileChildren(args: {
  displayName: string;
  profile: KPM;
  citations: Chat.Citation[];
  hasPredictiveScheduling: boolean;
  hcpProfileElements: {
    contactInformation?: boolean;
    interactionsSummary?: boolean;
    leaderTopics?: boolean;
    keyInsights?: boolean;
    conferencePresence?: boolean;
    keyDiscussionQuestions?: boolean;
  };
  renderTextField: RenderTextField;
  renderPrimaryAffiliation: () => ReactNode;
  renderRecommendationReason: () => ReactNode;
  renderContactDetails: () => ReactNode;
  renderInteractionsSummary: () => ReactNode;
  renderTopics: () => ReactNode;
  renderLeadershipField: () => ReactNode;
  renderTextListField: RenderTextListField;
  renderConferencePresence: () => ReactNode;
  renderQuickActions: () => ReactNode;
  renderGuidelines: () => ReactNode;
}): ReactNode {
  const {
    displayName,
    profile,
    citations,
    hasPredictiveScheduling,
    hcpProfileElements,
    renderTextField,
    renderPrimaryAffiliation,
    renderRecommendationReason,
    renderContactDetails,
    renderInteractionsSummary,
    renderTopics,
    renderLeadershipField,
    renderTextListField,
    renderConferencePresence,
    renderQuickActions,
    renderGuidelines,
  } = args;

  return (
    <>
      {renderTextField({
        label: 'Name',
        value: displayName,
      })}
      {renderTextField({
        label: 'Designation',
        value: profile.title,
      })}
      {renderPrimaryAffiliation()}
      {renderRecommendationReason()}
      {hcpProfileElements.contactInformation && renderContactDetails()}
      {hasPredictiveScheduling && <AvailabilityInsights citations={citations} profile={profile} />}
      {hcpProfileElements.interactionsSummary && renderInteractionsSummary()}
      {hcpProfileElements.leaderTopics && renderTopics()}
      {renderLeadershipField()}

      {hcpProfileElements.keyInsights &&
        renderTextListField({
          label: 'Key Insights',
          items: profile.keyInsights.map((m) => ({ value: <BasicMarkdown>{m}</BasicMarkdown> })),
          aiGenerated: `Key insights are AI generated based on the following information:
• HCP's profile and activity
• HCP's interactions with MSLs
• HCP patients' unmet needs
• Strategic imperatives`,
        })}
      {hcpProfileElements.conferencePresence && renderConferencePresence()}
      {hcpProfileElements.keyDiscussionQuestions &&
        renderTextListField({
          label: 'Key Discussion Questions',
          items: profile.keyDiscussionQuestions.map((m) => ({ value: <BasicMarkdown>{m}</BasicMarkdown> })),
          aiGenerated: `Key discussion questions are generated based on the following information:
• HCP's profile and activity
• HCP's interactions with MSLs
• HCP patients' unmet needs
• Strategic imperatives`,
        })}
      {renderQuickActions()}
      {renderGuidelines()}
    </>
  );
}

const KolProfileMessage = ({ followupQuestions, kolSearchId, profile, queryId }: Props) => {
  const [profileRef, setProfileRef] = useState<HTMLDivElement | null>(null);
  const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);
  const citations = useContext(CitationsContext);
  const chat = useContext(ActiveChatSessionContext);
  const { copyChatMessages, exportChatData, followUps } = useGroupFeatures();

  const apiClient = useChatApiClient();

  const { download } = useDownloadKolProfile({
    kol: {
      id: profile.id,
      name: profile.name,
    },
  });

  const copyToClipboard = useCopyToClipboard();

  const copy = `Sure, here is the HCP profile for "${profile.name}"`;

  const renderProfileLink = useCallback(() => {
    if (!profile.profileLink) return null;

    return (
      <>
        (
        <StyledAnchor href={profile.profileLink} target="_blank" rel="noreferrer">
          Link
        </StyledAnchor>
        )
      </>
    );
  }, [profile.profileLink]);

  const handleCopy = useCallback(() => {
    if (queryId) {
      return apiClient.queries
        .getQueryResponseText({
          chatInstanceId: chat.id,
          queryId,
          tts: false,
        })
        .then(copyToClipboard);
    }
    if (kolSearchId) {
      return apiClient.kolSearch
        .getKolSearchProfileText({
          kolSearchId,
          tts: false,
        })
        .then(copyToClipboard);
    }
  }, [apiClient.kolSearch, apiClient.queries, chat.id, copyToClipboard, kolSearchId, queryId]);

  const handleDownload = useCallback(() => {
    download({
      title: `Generating Export`,
      name: `${profile.name} Profile`,
      extension: ReportFormat.Word,
    }).catch(() => undefined);
  }, [download, profile.name]);

  const relatedQuestions = useMemo(
    () =>
      followupQuestions.map((f) => ({
        displayValue: f,
        type: ChatHintType.Text,
      })),
    [followupQuestions]
  );

  useAutoPlayReadAloud({
    kolSearchId,
    hasContent: !!profileRef?.innerHTML,
    queryId,
  });

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <MessageBody>
          <Copy>
            {copy} {renderProfileLink()}
          </Copy>

          <KolProfileDetails
            ref={setProfileRef}
            citations={citations}
            profile={profile}
            queryId={queryId}
            kolSearchId={kolSearchId}
          />

          <Footer>
            {(profile.profileConfig
              ? profile.messageActions?.download && exportChatData
              : exportChatData) && <BaseDownloadButton onClick={handleDownload} />}
            {(profile.profileConfig
              ? profile.messageActions?.copy && copyChatMessages
              : copyChatMessages) && <CopyButton onClick={handleCopy} />}
            {(profile.profileConfig ? profile.messageActions?.readAloud : true) && (
              <ReadAloudButton queryId={queryId} kolSearchId={kolSearchId} />
            )}
          </Footer>

          <StyledCitationsPane
            title={`Sources (${getCitationsDisplayCount(citations)})`}
            open={citationsOpen}
            toggleOpen={toggleCitationsOpen}
          >
            <StyledMessageCitations items={citations} />
          </StyledCitationsPane>

          {followUps && (
            <StyledRelatedQuestions
              profileKey="hcp"
              queryIdentifier={kolSearchId?.toString()}
              hints={relatedQuestions}
            />
          )}
        </MessageBody>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

type Params = {
  queryId?: number | null;
  hasContent?: boolean;
  kolSearchId?: number | null;
};

const useAutoPlayReadAloud = ({ hasContent, kolSearchId, queryId }: Params) => {
  const initializedRef = useRef(false);

  const initiateReadAloud = useInitiateQuerySpeechToTextNullSafe();
  const stopTextToSpeech = useStopTextToSpeechContextNullSafe();
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);

  const isPlaying =
    audioState?.state === 'playing' &&
    (audioState.queryId === queryId || audioState.kolSearchId === kolSearchId);

  const handleClick = useCallback(async () => {
    if (isPlaying) {
      stopTextToSpeech();
      return;
    }

    if (!initiateReadAloud) {
      return;
    }

    return initiateReadAloud({
      kolSearchId: kolSearchId ?? null,
      queryId: queryId ?? null,
    });
  }, [isPlaying, stopTextToSpeech, initiateReadAloud, kolSearchId, queryId]);

  useEffect(() => {
    if (!hasContent || initializedRef.current) {
      return;
    }

    if (
      voicemode?.enabled &&
      voicemode?.queryId === queryId &&
      voicemode?.type === ChatResponseType.HcpSummary
    ) {
      if (!audioState) {
        return;
      }

      if (audioState?.state === 'playing') {
        stopTextToSpeech();
      }

      handleClick().catch(() => undefined);
      voicemode?.dequeue();
      initializedRef.current = true;
    }
  }, [audioState, handleClick, hasContent, queryId, stopTextToSpeech, voicemode]);
};

const KolProfileMessageContainer = () => {
  const citations = useContext(CitationsContext);
  const profile = useContext(KolProfileContext);
  const kolSearchId = useContext(KolSearchIdContext);
  const followupQuestions = useContext(KolFollowupQuestionsContext);

  return (
    <SystemMessageContainer citations={citations}>
      <KolProfileMessage followupQuestions={followupQuestions} profile={profile} kolSearchId={kolSearchId} />
    </SystemMessageContainer>
  );
};

export { KolProfileMessageContainer, KolProfileMessage };

type DetailsProps = {
  citations: Chat.Citation[];
  kolSearchId?: number;
  profile: KPM;
  queryId?: number;
};

const KolProfileDetails = memo(
  forwardRef<HTMLDivElement, DetailsProps>(({ citations, profile, queryId, kolSearchId }, ref) => {
    const [hoveringSection, setHoveringSection] = useState<string | null>(null);

    const handleMouseEnterSection = useCallback(
      (label: string) => () => {
        setHoveringSection(label);
      },
      []
    );

    const handleMouseLeaveSection = useCallback(() => {
      setHoveringSection(null);
    }, []);

    const setChatInputText = useSetChatInputText();
    const { mutate: submitQuery } = useSubmitQueryMutation();
    const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();

    const groupFeatures = useContext(GroupFeaturesContext);
    const hcpProfileElements = groupFeatures?.hcpProfileElements;
    const showDirectSubmitLearnMore = profile.profileConfig
      ? (profile.messageActions?.learnMoreDirectSubmit ?? false)
      : false;

    const onDirectSubmitLearnMore = useCallback(
      (question: string) => {
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) {
          return;
        }

        const parentQueryIdentifier = getSelectedQueryParentIdentifier();

        submitQuery({
          identifier: createId(),
          parentQueryIdentifier: parentQueryIdentifier ?? null,
          query: trimmedQuestion ?? null,
          action: null,
        });
      },
      [submitQuery, getSelectedQueryParentIdentifier]
    );

    const renderTextField = useCallback((props: RenderTextProps) => renderProfileTextField(props), []);

    const renderTextListField = useCallback(
      (props: RenderTextListProps) =>
        renderProfileTextList(
          {
            hoveringSection,
            handleMouseEnterSection,
            handleMouseLeaveSection,
            showDirectSubmitLearnMore,
            onDirectSubmitLearnMore,
          },
          props
        ),
      [
        hoveringSection,
        handleMouseEnterSection,
        handleMouseLeaveSection,
        showDirectSubmitLearnMore,
        onDirectSubmitLearnMore,
      ]
    );

    const renderLeadershipField = useCallback(
      () =>
        renderLeadershipFieldSection({
          profile,
          citations,
          hcpProfileElements: hcpProfileElements ?? {},
          renderTextListField,
        }),
      [citations, hcpProfileElements, profile, renderTextListField]
    );

    const renderInteractionsSummary = useCallback(
      () =>
        renderInteractionsSummarySection({
          profile,
          citations,
          renderTextListField,
        }),
      [citations, profile, renderTextListField]
    );

    const renderTopics = useCallback(
      () =>
        renderTopicsSection({
          profile,
          citations,
          renderTextListField,
        }),
      [citations, profile, renderTextListField]
    );

    const displayName = useMemo(() => getProfileDisplayName(profile), [profile]);

    const renderConferencePresence = useCallback(
      () =>
        renderConferencePresenceSection({
          profile,
          citations,
          renderTextListField,
        }),
      [citations, profile, renderTextListField]
    );

    const renderGuidelines = useCallback(() => {
      const copy =
        profile.disclaimer ??
        `Metrics are based on available data sources, which may not fully represent HCP activity. Please leverage this information directionally to supplement your local market knowledge.`;

      return <Guidelines>{copy}</Guidelines>;
    }, [profile.disclaimer]);

    const renderRecommendationReason = useCallback(
      () => renderRecommendationReasonSection(profile),
      [profile]
    );

    const renderContactDetails = useCallback(
      () =>
        renderContactDetailsSection({
          profile,
          citations,
          renderTextListField,
        }),
      [citations, profile, renderTextListField]
    );

    const renderPrimaryAffiliation = useCallback(
      () =>
        renderPrimaryAffiliationSection({
          profile,
          citations,
          renderTextField,
        }),
      [citations, profile, renderTextField]
    );

    const quickActions = useMemo(() => buildQuickActions(profile.quickActions || []), [profile.quickActions]);

    const handleQuickActionClick = useCallback(
      (value: string) => {
        setChatInputText(value, { focus: true });
      },
      [setChatInputText]
    );

    const renderQuickActions = useCallback(
      () =>
        renderQuickActionsSection({
          actions: quickActions,
          onActionClick: handleQuickActionClick,
        }),
      [quickActions, handleQuickActionClick]
    );

    const hasPredictiveScheduling = useHasPredictiveScheduling();

    const sectionRenderers: Record<string, () => ReactNode> = useMemo(
      () => ({
        hcp_contact_information: () => renderContactDetails(),
        hcp_availability_insights: () => <AvailabilityInsights citations={citations} profile={profile} />,
        hcp_interaction_total: () => renderInteractionsSummary(),
        hcp_leader_topics: () => renderTopics(),
        hcp_clinical_publications: () => renderLeadershipField(),
        hcp_key_insights: () =>
          renderTextListField({
            label: 'Key Insights',
            items: profile.keyInsights.map((m) => ({ value: <BasicMarkdown>{m}</BasicMarkdown> })),
            aiGenerated: `Key insights are AI generated based on the following information:
• HCP's profile and activity
• HCP's interactions with MSLs
• HCP patients' unmet needs
• Strategic imperatives`,
          }),
        hcp_conference_presence: () => renderConferencePresence(),
        hcp_discussion_questions: () =>
          renderTextListField({
            label: 'Key Discussion Questions',
            items: profile.keyDiscussionQuestions.map((m) => ({ value: <BasicMarkdown>{m}</BasicMarkdown> })),
            aiGenerated: `Key discussion questions are generated based on the following information:
• HCP's profile and activity
• HCP's interactions with MSLs
• HCP patients' unmet needs
• Strategic imperatives`,
          }),
        hcp_recommendations: () => renderRecommendationReason(),
      }),
      [
        citations,
        profile,
        renderContactDetails,
        renderInteractionsSummary,
        renderTopics,
        renderLeadershipField,
        renderTextListField,
        renderConferencePresence,
        renderRecommendationReason,
      ]
    );

    if (profile.profileConfig) {
      return (
        <div ref={ref}>
          {renderProfileConfigChildren({
            displayName,
            profile,
            renderTextField,
            renderPrimaryAffiliation,
            sectionRenderers,
            renderQuickActions,
            renderGuidelines,
          })}
        </div>
      );
    }

    return (
      <div ref={ref}>
        {renderDefaultProfileChildren({
          displayName,
          profile,
          citations,
          hasPredictiveScheduling,
          hcpProfileElements: hcpProfileElements ?? {},
          renderTextField,
          renderPrimaryAffiliation,
          renderRecommendationReason,
          renderContactDetails,
          renderInteractionsSummary,
          renderTopics,
          renderLeadershipField,
          renderTextListField,
          renderConferencePresence,
          renderQuickActions,
          renderGuidelines,
        })}
      </div>
    );
  })
);

type RenderTextListProps = {
  citations?: string[];
  label: string;
  items: ListItemProps[];
  learnMore?: string;
  aiGenerated?: string | null;
};

type RenderTextProps = {
  citations?: string[];
  label: string;
  value: ReactNode;
};

type ListItemProps = {
  citations?: string[];
  learnMore?: string;
  onDirectSubmitLearnMore?: (question: string) => void;
  showDirectSubmitLearnMore?: boolean;
  value: React.ReactNode;
};

const TextListItem = memo(
  ({
    citations = [],
    learnMore,
    value,
    showDirectSubmitLearnMore = false,
    onDirectSubmitLearnMore,
  }: ListItemProps) => {
    const [ref, isHovering] = useHover<HTMLLIElement>(!!learnMore);

    return (
      <StyledListItem ref={ref}>
        {value}
        {!!citations.length && citations.map((c) => <Citation key={c} value={c} />)}
        {learnMore && (
          <LearnMore
            open={isHovering}
            question={learnMore}
            showDirectSubmit={showDirectSubmitLearnMore}
            onDirectSubmit={onDirectSubmitLearnMore}
          />
        )}
      </StyledListItem>
    );
  }
);

const BulletedLabel = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.black.light1,
}));
